// Legacy media timeline repair.
//
// Video/audio boxes authored before the timeline feature carry no box.timeline
// at all. The editor timeline and the visor both tolerate that — they derive a
// window on the fly (resolveBoxTiming + a media-duration probe) — but the
// recorder does not: common/sceneTimeline.es6 reads box.timeline.startFrame /
// endFrame directly, so a box with no persisted window yields
// (endFrame - startFrame) === 0 and is dropped from the export audio manifest,
// and never extends the scene duration either.
//
// This module finds those boxes, probes each media file once for its real
// duration and produces the timeline patch the rest of the app was only ever
// computing in memory. Persisting it fixes the export without changing what
// anything else already displays.

import { resolveBoxTiming, resolveBoxEndFrame } from "../../common/resolveBoxTiming.es6";
import { FPS } from "../../common/sceneTimeline.es6";

// The three plugin ids common/sceneTimeline.es6 turns into manifest tracks —
// repairing anything else would change scene durations without fixing audio.
export const VIDEO_PLUGIN_IDS = ["EnrichedPlayer"];
export const AUDIO_PLUGIN_IDS = ["EnrichedAudio", "AudioCue"];

function mediaTypeOf(pluginId) {
  if (VIDEO_PLUGIN_IDS.includes(pluginId)) return "video";
  if (AUDIO_PLUGIN_IDS.includes(pluginId)) return "audio";
  return null;
}

/**
 * Media boxes whose end frame cannot be resolved from persisted state.
 * A box with an explicit timeline.endFrame — or a legacy opacity-keyframe end —
 * already has an authored window and is never touched.
 */
export function findLegacyMediaBoxes(boxes = {}, pluginToolbars = {}, animationsById = {}) {
  const found = [];
  for (const boxId of Object.keys(boxes)) {
    const toolbar = pluginToolbars[boxId];
    if (!toolbar) continue;
    const type = mediaTypeOf(toolbar.pluginId);
    if (!type) continue;
    const url = toolbar.state && toolbar.state.url;
    if (!url) continue;
    const anims = animationsById[boxId] || [];
    if (resolveBoxEndFrame(boxes[boxId], null, anims, FPS) !== null) continue;
    found.push({ boxId, parent: boxes[boxId].parent, pluginId: toolbar.pluginId, type, url });
  }
  return found;
}

/**
 * The timeline window a probed duration implies — the same rule buildLayers
 * paints with, so the repair persists exactly what the user already sees.
 */
export function buildTimelinePatch(box, durationSec, anims = []) {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return null;
  const endFrame = resolveBoxEndFrame(box, Math.ceil(durationSec * FPS), anims, FPS);
  if (typeof endFrame !== "number" || endFrame <= 0) return null;
  const { startFrame } = resolveBoxTiming(box, anims, FPS);
  return { startFrame: typeof startFrame === "number" ? startFrame : 0, endFrame };
}

// Probe a handful of files at a time: one <video>/<audio> metadata load per
// unique url, but 65 of them in parallel would stall the network.
async function probeDurations(urls, probe, concurrency, onProgress) {
  const durations = new Map();
  let index = 0;
  let done = 0;
  const worker = async () => {
    while (index < urls.length) {
      const { url, type } = urls[index++];
      let duration = null;
      try {
        duration = await probe(url, type);
      } catch (e) {
        duration = null;
      }
      durations.set(url, duration);
      done++;
      if (onProgress) onProgress({ done, total: urls.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, urls.length) }, worker));
  return durations;
}

/**
 * Scan + probe. Returns the patch map for repairMediaTimelines(patches) plus a
 * report the UI can show. Dispatching is left to the caller.
 *
 * @param probe async (url, type) => durationSec | null
 */
export async function collectMediaTimelinePatches({
  boxes = {},
  pluginToolbars = {},
  animationsById = {},
  probe,
  concurrency = 4,
  onProgress,
}) {
  const candidates = findLegacyMediaBoxes(boxes, pluginToolbars, animationsById);
  const uniqueUrls = [];
  const seen = new Set();
  for (const c of candidates) {
    if (seen.has(c.url)) continue;
    seen.add(c.url);
    uniqueUrls.push({ url: c.url, type: c.type });
  }

  const durations = await probeDurations(uniqueUrls, probe, concurrency, onProgress);

  const patches = {};
  const failed = [];
  for (const c of candidates) {
    const patch = buildTimelinePatch(boxes[c.boxId], durations.get(c.url), animationsById[c.boxId]);
    if (patch) patches[c.boxId] = patch;
    else failed.push(c);
  }

  return {
    patches,
    scanned: candidates.length,
    repaired: Object.keys(patches).length,
    failed,
    scenes: new Set(Object.keys(patches).map((id) => boxes[id] && boxes[id].parent)).size,
  };
}
